import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Form } from "react-bootstrap";
import { NumericFormat } from "react-number-format";
import deletebtn from "../../../images/delete4.png";
import addbtn from "../../../images/plus4.png";
import "./OfficersIncExc.scss";

const defaultInitialOfficer = {
  state: "",
  locNumber: "",
  name: "",
  ownership: "",
  duties: "Executive",
  inclusionType: "",
  classCode: "",
  payroll: "",
  stateError: "",
  classCodeError: "",
};

const extractLocationData = (childrenLoc = {}) => {
  const stateMap = {};

  Object.values(childrenLoc || {}).forEach((loc) => {
    const stateVal =
      loc?.state?.value?.trim()?.toUpperCase() ||
      (typeof loc?.state === "string" ? loc?.state?.trim()?.toUpperCase() : "");

    if (stateVal) {
      if (!stateMap[stateVal]) {
        stateMap[stateVal] = new Set();
      }

      if (loc?.classCodesInfo) {
        Object.values(loc?.classCodesInfo).forEach((cc) => {
          const desc = cc?.classCodeDescription?.value?.trim();
          if (desc) {
            const code = desc.split(":")[0]?.trim();
            if (code) {
              stateMap[stateVal].add(code);
            }
          }
        });
      }
    }
  });

  return {
    availableStates: Object.keys(stateMap).sort(),
    ccForState: (state) => {
      if (!state) return [];
      const norm = state?.trim()?.toUpperCase();
      return stateMap[norm] ? Array.from(stateMap[norm]) : [];
    },
  };
};

const OfficersIncExc = ({
  officersList: initialOfficersList,
  childrenLoc = {},
  updateOfficers,
  disabled = false,
}) => {
  const [officersList, setOfficersList] = useState(() => {
    if (initialOfficersList && initialOfficersList?.length > 0) {
      return initialOfficersList?.map((off) => ({
        state: off?.state || "",
        locNumber: off?.locNumber !== undefined ? String(off?.locNumber) : "",
        name: off?.name || "",
        ownership: off?.ownership !== undefined ? String(off?.ownership) : "",
        duties: off?.duties !== undefined ? off?.duties : "Executive",
        inclusionType: off?.inclusionType || "",
        classCode: off?.classCode || "",
        payroll: off?.payroll !== undefined ? String(off?.payroll) : "",
        stateError: "",
        classCodeError: "",
      }));
    }
    return [{ ...defaultInitialOfficer }];
  });

  // Extract available states and class codes for states in a single pass
  const { availableStates, ccForState } = useMemo(
    () => extractLocationData(childrenLoc),
    [childrenLoc]
  );

  // Field-specific validation
  const validateOfficer = useCallback(
    (officer) => {
      const enteredCode = officer?.classCode
        ? officer?.classCode?.toString()?.trim()
        : "";
      const selectedState = officer?.state
        ? officer?.state?.trim()?.toUpperCase()
        : "";

      let stateError = "";
      let classCodeError = "";

      // State validation
      if (selectedState && !availableStates?.includes(selectedState)) {
        stateError = `State "${selectedState}" does not exist in Locations.`;
      }

      // Class code validation
      if (enteredCode && !selectedState) {
        classCodeError = "Please select a State first.";
      } else if (selectedState && enteredCode) {
        const availableCodes = ccForState ? ccForState(selectedState) : [];
        if (!availableCodes?.includes(enteredCode)) {
          classCodeError = `Class Code "${enteredCode}" does not exist for State "${selectedState}".`;
        }
      }

      return { stateError, classCodeError };
    },
    [availableStates, ccForState]
  );

  // Notify parent with clean fields
  const notifyParent = useCallback(
    (updatedList) => {
      let isValid = true;
      const validatedList = updatedList?.map((officer) => {
        const { stateError, classCodeError } = validateOfficer(officer);
        if (stateError || classCodeError) isValid = false;
        return { ...officer, stateError, classCodeError };
      });

      setOfficersList(validatedList);

      if (updateOfficers) {
        const cleanList = validatedList?.map((officer) => ({
          state: officer?.state || "",
          locNumber: officer?.locNumber || "",
          name: officer?.name || "",
          ownership: officer?.ownership || "",
          duties: officer?.duties !== undefined ? officer?.duties : "Executive",
          inclusionType: officer?.inclusionType || "",
          classCode: officer?.classCode || "",
          payroll: officer?.payroll || "",
        }));
        updateOfficers(cleanList, isValid);
      }
    },
    [validateOfficer, updateOfficers]
  );

  // Sync when props.officersList changes
  useEffect(() => {
    if (initialOfficersList && initialOfficersList?.length > 0) {
      setOfficersList((prev) => {
        const incomingStr = JSON.stringify(
          initialOfficersList?.map((off) => ({
            state: off?.state || "",
            locNumber: off?.locNumber !== undefined ? String(off?.locNumber) : "",
            name: off?.name || "",
            ownership: off?.ownership !== undefined ? String(off?.ownership) : "",
            duties: off?.duties !== undefined ? off?.duties : "Executive",
            inclusionType: off?.inclusionType || "",
            classCode: off?.classCode || "",
            payroll: off?.payroll !== undefined ? String(off?.payroll) : "",
          }))
        );
        const currentStr = JSON.stringify(
          prev?.map((off) => ({
            state: off?.state || "",
            locNumber: off?.locNumber || "",
            name: off?.name || "",
            ownership: off?.ownership !== undefined ? String(off?.ownership) : "",
            duties: off?.duties !== undefined ? off?.duties : "Executive",
            inclusionType: off?.inclusionType || "",
            classCode: off?.classCode || "",
            payroll: off?.payroll !== undefined ? String(off?.payroll) : "",
          }))
        );
        if (incomingStr !== currentStr) {
          return initialOfficersList?.map((off) => {
            const { stateError, classCodeError } = validateOfficer(off);
            return {
              state: off?.state || "",
              locNumber: off?.locNumber !== undefined ? String(off?.locNumber) : "",
              name: off?.name || "",
              ownership: off?.ownership !== undefined ? String(off?.ownership) : "",
              duties: off?.duties !== undefined ? off?.duties : "Executive",
              inclusionType: off?.inclusionType || "",
              classCode: off?.classCode || "",
              payroll: off?.payroll !== undefined ? String(off?.payroll) : "",
              stateError,
              classCodeError,
            };
          });
        }
        return prev;
      });
    }
  }, [initialOfficersList, validateOfficer]);

  // Re-validate if childrenLoc changes
  useEffect(() => {
    setOfficersList((prev) =>
      prev?.map((off) => {
        const { stateError, classCodeError } = validateOfficer(off);
        return { ...off, stateError, classCodeError };
      })
    );
  }, [childrenLoc, validateOfficer]);

  // Handlers
  const handleAddOfficer = () => {
    const updatedList = [...officersList, { ...defaultInitialOfficer }];
    notifyParent(updatedList);
  };

  const handleRemoveOfficer = (index) => {
    const updatedList = officersList.filter((_, i) => i !== index);
    notifyParent(updatedList);
  };

  const handleFieldChange = (index, field, value) => {
    const updatedList = [...officersList];
    updatedList[index] = {
      ...updatedList[index],
      [field]: value,
    };
    notifyParent(updatedList);
  };

  return (
    <div className="officers-container">
      <div className="pink-header sub_headings">
        <div className="font-family-montserrat">
          Individuals Included/Excluded
        </div>
      </div>

      <div className="officers-rows-wrapper">
        {officersList.map((officer, index) => (
          <div key={index} className="officer-row">
            <div className="row grey loc1street_margin align-items-start no-gutters">
              {/* 0. Row Label (Officer - 1, Officer - 2, ...) */}
              <div className="officer-label-col">
                <p>Officer - {index + 1}</p>
              </div>

              {/* 1. State Dropdown */}
              <div className="officer-state-col">
                <Form.Group className="form-group mb-0">
                  <label className="font-family-montserrat-semi-bold">
                    State
                  </label>
                  <select
                    disabled={disabled || (availableStates?.length || 0) === 0}
                    className={`rectangle1 ${
                      officer?.state
                        ? "officer-select-selected"
                        : "officer-select-placeholder"
                    } ${officer?.stateError ? "officer-input-error" : ""}`}
                    value={officer?.state || ""}
                    onChange={(e) =>
                      handleFieldChange(index, "state", e.target.value)
                    }
                  >
                    <option value="" className="officer-option-default">
                      {(availableStates?.length || 0) === 0
                        ? "No State"
                        : "Select"}
                    </option>
                    {availableStates?.map((st) => (
                      <option
                        key={st}
                        value={st}
                        className="officer-option-item"
                      >
                        {st}
                      </option>
                    ))}
                  </select>
                  {officer?.stateError && (
                    <p className="errMsg officer-error-msg">
                      {officer?.stateError}
                    </p>
                  )}
                </Form.Group>
              </div>

              {/* 2. Loc# Input Field */}
              <div className="officer-loc-col">
                <Form.Group className="form-group mb-0">
                  <label className="font-family-montserrat-semi-bold">
                    Loc#
                  </label>
                  <NumericFormat
                    disabled={disabled}
                    className="rectangle1"
                    placeholder="Loc#"
                    maxLength={3}
                    decimalScale={0}
                    allowNegative={false}
                    value={officer?.locNumber || ""}
                    onValueChange={(values) =>
                      handleFieldChange(index, "locNumber", values.value)
                    }
                    isAllowed={(values) => {
                      const { value } = values;
                      return value === "" || (/^\d+$/.test(value) && value.length <= 3);
                    }}
                    autoComplete="off"
                  />
                </Form.Group>
              </div>

              {/* 3. Name Input Field */}
              <div className="officer-name-col">
                <Form.Group className="form-group mb-0">
                  <label className="font-family-montserrat-semi-bold">
                    Name
                  </label>
                  <input
                    type="text"
                    disabled={disabled}
                    className="rectangle1"
                    placeholder="Name"
                    value={officer?.name || ""}
                    onChange={(e) =>
                      handleFieldChange(index, "name", e.target.value)
                    }
                    autoComplete="off"
                  />
                </Form.Group>
              </div>

              {/* 4. Ownership % Input Field */}
              <div className="officer-ownership-col">
                <Form.Group className="form-group mb-0">
                  <label className="font-family-montserrat-semi-bold">
                    Ownership
                  </label>
                  <NumericFormat
                    disabled={disabled}
                    className="rectangle1"
                    placeholder="Enter Value"
                    suffix="%"
                    value={officer?.ownership || ""}
                    onValueChange={(values) =>
                      handleFieldChange(index, "ownership", values.value)
                    }
                    isAllowed={(values) => {
                      const { floatValue } = values;
                      return (
                        floatValue === undefined ||
                        (floatValue >= 0 && floatValue <= 100)
                      );
                    }}
                    autoComplete="off"
                  />
                </Form.Group>
              </div>

              {/* 5. Duties Input Field */}
              <div className="officer-duties-col">
                <Form.Group className="form-group mb-0">
                  <label className="font-family-montserrat-semi-bold">
                    Duties
                  </label>
                  <input
                    type="text"
                    disabled={disabled}
                    className="rectangle1"
                    placeholder="Duties"
                    value={officer?.duties !== undefined ? officer?.duties : "Executive"}
                    onChange={(e) =>
                      handleFieldChange(index, "duties", e.target.value)
                    }
                    autoComplete="off"
                  />
                </Form.Group>
              </div>

              {/* 6. Included / Excluded Dropdown */}
              <div className="officer-inclusion-col">
                <Form.Group className="form-group mb-0">
                  <label className="font-family-montserrat-semi-bold">
                    Included/Excluded
                  </label>
                  <select
                    disabled={disabled}
                    className={`rectangle1 ${
                      officer?.inclusionType
                        ? "officer-select-selected"
                        : "officer-select-placeholder"
                    }`}
                    value={officer?.inclusionType || ""}
                    onChange={(e) =>
                      handleFieldChange(index, "inclusionType", e.target.value)
                    }
                  >
                    <option value="" className="officer-option-default">
                      Select
                    </option>
                    <option value="INCLUDED" className="officer-option-item">
                      Included
                    </option>
                    <option value="EXCLUDED" className="officer-option-item">
                      Excluded
                    </option>
                  </select>
                </Form.Group>
              </div>

              {/* 7. Class Code Input Field */}
              <div className="officer-classcode-col">
                <Form.Group className="form-group mb-0">
                  <label className="font-family-montserrat-semi-bold">
                    Class Code
                  </label>
                  <input
                    type="text"
                    disabled={disabled}
                    className={`rectangle1 ${
                      officer?.classCodeError ? "officer-input-error" : ""
                    }`}
                    placeholder="Class Code"
                    value={officer?.classCode || ""}
                    onChange={(e) =>
                      handleFieldChange(index, "classCode", e.target.value)
                    }
                    autoComplete="off"
                  />
                  {officer?.classCodeError && (
                    <p className="errMsg officer-error-msg">
                      {officer?.classCodeError}
                    </p>
                  )}
                </Form.Group>
              </div>

              {/* 8. Payroll Input Field */}
              <div className="officer-payroll-col">
                <Form.Group className="form-group mb-0">
                  <label className="font-family-montserrat-semi-bold">
                    Payroll
                  </label>
                  <NumericFormat
                    disabled={disabled}
                    className="rectangle1"
                    placeholder="Payroll"
                    prefix="$ "
                    thousandSeparator=","
                    decimalScale={0}
                    allowNegative={false}
                    value={officer?.payroll || ""}
                    onValueChange={(values) =>
                      handleFieldChange(index, "payroll", values.value)
                    }
                    isAllowed={(values) => {
                      const { value } = values;
                      return value === "" || /^\d+$/.test(value);
                    }}
                    autoComplete="off"
                  />
                </Form.Group>
              </div>

              {/* 9. Delete Action */}
              <div className="officer-action-col">
                {officersList.length > 1 && (
                  <button
                    type="button"
                    disabled={disabled}
                    className="btnInvisible transpButton"
                    onClick={() => handleRemoveOfficer(index)}
                    title="Remove Officer"
                  >
                    <img
                      src={deletebtn}
                      alt="Delete"
                      className="delete_records_btn"
                    />
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Add Officer Button */}
      <div className="officer-add-btn-wrapper">
        <button
          disabled={disabled}
          type="button"
          className="btnInvisible addLoc transpButton p-0"
          onClick={handleAddOfficer}
        >
          <img src={addbtn} alt="Add" /> <b>Add Officer</b>
        </button>
      </div>
    </div>
  );
};

export default OfficersIncExc;
